import { jalaliMonthName, toJalali } from './jalali';

/* -------------------------------------------------------------------------- */
/* Digits                                                                      */
/* -------------------------------------------------------------------------- */

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] as const;

/** Render Latin digits in a string as Persian ones. Non-digits pass through. */
export function toPersianDigits(input: string | number): string {
  return String(input).replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)]!);
}

/**
 * Normalise Persian/Arabic-Indic digits to Latin. Every user-typed numeric
 * field (phone, OTP, amount, join code) runs through this before validation —
 * Persian keyboards produce Persian digits and the backend only accepts Latin.
 */
export function toLatinDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}

const integerFormatter = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 0 });

/** Format a plain count with Persian digits and grouping, e.g. `۱٬۲۳۴`. */
export function formatNumber(value: number | null | undefined): string {
  return integerFormatter.format(value ?? 0);
}

/** Format a ratio in 0–1 as a signed percentage, e.g. `٪۳+`. */
export function formatPercent(ratio: number, { signed = false } = {}): string {
  const percent = Math.round(ratio * 100);
  const sign = signed && percent > 0 ? '+' : '';
  return `${sign}٪${formatNumber(percent)}`;
}

/* -------------------------------------------------------------------------- */
/* Dates                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Parse an API date. The backend returns `date` as `YYYY-MM-DD` and `datetime`
 * as ISO 8601. A bare `YYYY-MM-DD` is parsed by JS as **UTC midnight**, which
 * shifts a day backwards in Iran's timezone, so those are parsed as local.
 */
export function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Serialise a `Date` as the `YYYY-MM-DD` the API expects for `date` fields. */
export function toApiDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `۱۴۰۴/۰۹/۲۰` — the compact form used in tables and detail rows. */
export function formatJalaliDate(value: string | Date | null | undefined): string {
  const date = value instanceof Date ? value : parseApiDate(value);
  if (!date) return '—';
  const { year, month, day } = toJalali(date);
  const pad = (n: number) => toPersianDigits(String(n).padStart(2, '0'));
  return `${toPersianDigits(year)}/${pad(month)}/${pad(day)}`;
}

/** `۲۰ آذر ۱۴۰۴` — the readable form used in headings and empty states. */
export function formatJalaliDateLong(value: string | Date | null | undefined): string {
  const date = value instanceof Date ? value : parseApiDate(value);
  if (!date) return '—';
  const { year, month, day } = toJalali(date);
  return `${toPersianDigits(day)} ${jalaliMonthName(month)} ${toPersianDigits(year)}`;
}

/** `۲۰ آذر ۱۴۰۴، ۱۴:۳۰` — for audit trails where the time matters. */
export function formatJalaliDateTime(value: string | Date | null | undefined): string {
  const date = value instanceof Date ? value : parseApiDate(value);
  if (!date) return '—';
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return `${formatJalaliDateLong(date)}، ${toPersianDigits(time)}`;
}

/** `مهر ماه ۱۴۰۴` — how a charge period is labelled throughout the app. */
export function formatChargePeriod(year: number, month: number): string {
  return `${jalaliMonthName(month)} ماه ${toPersianDigits(year)}`;
}

const RELATIVE_UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

const relativeFormatter = new Intl.RelativeTimeFormat('fa-IR', { numeric: 'auto' });

/** `۳ روز پیش`. Used where recency matters more than the exact timestamp. */
export function formatRelativeTime(value: string | Date | null | undefined): string {
  const date = value instanceof Date ? value : parseApiDate(value);
  if (!date) return '—';
  const diff = date.getTime() - Date.now();
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(diff) >= ms) return relativeFormatter.format(Math.round(diff / ms), unit);
  }
  return 'همین حالا';
}

/* -------------------------------------------------------------------------- */
/* People & identifiers                                                        */
/* -------------------------------------------------------------------------- */

interface NameParts {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
}

/** Best available display name, degrading to the phone number, then a dash. */
export function formatFullName(person: NameParts | null | undefined): string {
  if (!person) return '—';
  const name = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
  if (name) return name;
  return person.phone ? formatPhone(person.phone) : 'کاربر بی‌نام';
}

/** Up to two initials for avatar fallbacks. */
export function formatInitials(person: NameParts | null | undefined): string {
  if (!person) return '؟';
  const parts = [person.first_name, person.last_name].filter((p): p is string =>
    Boolean(p?.trim()),
  );
  if (parts.length === 0) return person.phone ? toPersianDigits(person.phone.slice(-2)) : '؟';
  return parts
    .slice(0, 2)
    .map((p) => p.trim().charAt(0))
    .join('');
}

/**
 * Wrap a string in LEFT-TO-RIGHT ISOLATE … POP DIRECTIONAL ISOLATE.
 *
 * A phone number grouped with spaces is three separate numeric runs to the
 * Unicode bidi algorithm; inside RTL text the runs are laid out right-to-left,
 * so `0919 145 9851` visually becomes `9851 145 0919`. The isolate pair forces
 * the whole token to keep its internal left-to-right order wherever it lands.
 */
// Built via fromCharCode so no invisible characters live in this source file.
const LTR_ISOLATE = String.fromCharCode(0x2066); // U+2066 LRI
const POP_ISOLATE = String.fromCharCode(0x2069); // U+2069 PDI

function isolateLtr(text: string): string {
  return `${LTR_ISOLATE}${text}${POP_ISOLATE}`;
}

/** `۰۹۱۲ ۳۴۵ ۶۷۸۹` — grouped for readability, Persian digits, bidi-safe. */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const digits = toLatinDigits(phone).replace(/\D/g, '');
  if (digits.length !== 11) return isolateLtr(toPersianDigits(phone));
  return isolateLtr(
    toPersianDigits(`${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`),
  );
}

/**
 * Join codes are shown exactly as issued.
 *
 * The backend generates them with `secrets.token_hex` (always lowercase) and
 * matches them **case-sensitively**. Prettifying the display — upper-casing it,
 * say — makes a manager read out a code that the resident then cannot use.
 */
export function formatJoinCode(code: string | null | undefined): string {
  return code ?? '—';
}
